import * as React from 'react'

/**
 * Shared mobile breakpoint used across the app.
 * Exported to keep a single source of truth for responsive logic.
 */
export const MOBILE_BREAKPOINT = 768

/**
 * Desktop breakpoint matching Tailwind's custom lg: breakpoint.
 * Below this is tablet/mobile territory.
 */
export const DESKTOP_BREAKPOINT = 1048

/**
 * Hook to determine if the current viewport is considered "mobile".
 * Uses the shared MOBILE_BREAKPOINT for consistency across the app.
 */
export function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = React.useState<boolean | undefined>(undefined)

  React.useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`)
    const onChange = (): void => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT)
    }
    mql.addEventListener('change', onChange)
    // Initialize state on mount
    setIsMobile(window.innerWidth < MOBILE_BREAKPOINT)
    return () => mql.removeEventListener('change', onChange)
  }, [])

  return !!isMobile
}

/**
 * Hook to determine if the current viewport is below desktop breakpoint.
 * Returns true for mobile AND tablet devices (below 1048px).
 */
export function useIsBelowDesktop(): boolean {
  const [isBelowDesktop, setIsBelowDesktop] = React.useState<boolean | undefined>(undefined)

  React.useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${DESKTOP_BREAKPOINT - 1}px)`)
    const onChange = (): void => {
      setIsBelowDesktop(window.innerWidth < DESKTOP_BREAKPOINT)
    }
    mql.addEventListener('change', onChange)
    setIsBelowDesktop(window.innerWidth < DESKTOP_BREAKPOINT)
    return () => mql.removeEventListener('change', onChange)
  }, [])

  return !!isBelowDesktop
}
